import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { Plus, Edit, Trash2, Eye, EyeOff, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import '@/styles/AdminManagement.css';

const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '', excerpt: '', content: '', category: '',
    image: '', readTime: '', link: '', published: true
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!adminApi.isAuthenticated()) { navigate('/admin/login'); return; }
    loadArticles();
  }, [navigate]);

  const loadArticles = async () => {
    try {
      const data = await adminApi.getAllArticles();
      setArticles(data);
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const openForm = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        title: article.title,
        excerpt: article.excerpt,
        content: article.content || '',
        category: article.category,
        image: article.image,
        readTime: article.readTime,
        link: article.link || '',
        published: article.published
      });
    } else {
      setEditingArticle(null);
      setFormData({
        title: '', excerpt: '', content: '', category: '',
        image: '', readTime: '5 min read', link: '', published: true
      });
    }
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle) {
        await adminApi.updateArticle(editingArticle.id, formData);
        setArticles(articles.map(a => a.id === editingArticle.id ? { ...editingArticle, ...formData } : a));
        toast({ title: "Article updated!" });
      } else {
        const newArticle = await adminApi.createArticle(formData);
        setArticles([...articles, newArticle]);
        toast({ title: "Article created!" });
      }
      setShowForm(false);
    } catch (error) {
      toast({ title: "Failed to save article", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await adminApi.deleteArticle(id);
      setArticles(articles.filter(a => a.id !== id));
      toast({ title: "Article deleted" });
    } catch (error) {
      toast({ title: "Failed to delete", variant: "destructive" });
    }
  };

  const togglePublish = async (article) => {
    try {
      const updated = { ...article, published: !article.published };
      await adminApi.updateArticle(article.id, updated);
      setArticles(articles.map(a => a.id === article.id ? updated : a));
      toast({ title: article.published ? "Unpublished" : "Published" });
    } catch (error) {
      toast({ title: "Failed to update", variant: "destructive" });
    }
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="sidebar-header"><h2>AK Admin</h2></div>
        <nav className="sidebar-nav">
          <button className="nav-item" onClick={() => navigate('/admin/dashboard')}>Dashboard</button>
          <button className="nav-item" onClick={() => navigate('/admin/contacts')}>Messages</button>
          <button className="nav-item" onClick={() => navigate('/admin/projects')}>Projects</button>
          <button className="nav-item active">Articles</button>
          <button className="nav-item" onClick={() => navigate('/admin/experience')}>Experience</button>
          <button className="nav-item" onClick={() => navigate('/admin/settings')}>Settings</button>
        </nav>
      </aside>

      <main className="admin-content">
        <div className="content-header">
          <h1>Manage Articles ({articles.length})</h1>
          <button className="add-btn" onClick={() => openForm()}>
            <Plus size={20} />Add Article
          </button>
        </div>

        <div className="items-grid">
          {articles.map((article) => (
            <div key={article.id} className="item-card">
              <img src={article.image} alt={article.title} className="item-image" />
              <div className="item-content">
                <div className="item-header">
                  <h3>{article.title}</h3>
                  <span className="item-category">{article.category}</span>
                </div>
                <p className="item-description">{article.excerpt}</p>
                <div className="item-meta">Views: {article.views || 0}</div>
                <div className="item-actions">
                  <button onClick={() => togglePublish(article)} className="action-btn">
                    {article.published ? <Eye size={16} /> : <EyeOff size={16} />}
                    {article.published ? 'Published' : 'Draft'}
                  </button>
                  <button onClick={() => openForm(article)} className="action-btn">
                    <Edit size={16} />Edit
                  </button>
                  <button onClick={() => handleDelete(article.id)} className="action-btn danger">
                    <Trash2 size={16} />Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showForm && (
          <div className="modal-overlay" onClick={() => setShowForm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingArticle ? 'Edit Article' : 'Add Article'}</h2>
                <button onClick={() => setShowForm(false)} className="close-btn"><X size={24} /></button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Title</label>
                    <input required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <input required value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} placeholder="Flutter, Architecture, Leadership" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Excerpt</label>
                  <textarea required rows="2" value={formData.excerpt} onChange={(e) => setFormData({...formData, excerpt: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Content (Full Article)</label>
                  <textarea required rows="6" value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Read Time</label>
                    <input required value={formData.readTime} onChange={(e) => setFormData({...formData, readTime: e.target.value})} placeholder="8 min read" />
                  </div>
                  <div className="form-group">
                    <label>Image URL</label>
                    <input required value={formData.image} onChange={(e) => setFormData({...formData, image: e.target.value})} />
                  </div>
                </div>
                <div className="form-group">
                  <label>External Link (optional)</label>
                  <input value={formData.link} onChange={(e) => setFormData({...formData, link: e.target.value})} placeholder="https://medium.com/..." />
                </div>
                <button type="submit" className="submit-btn">{editingArticle ? 'Update' : 'Create'} Article</button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminArticles;
